<!-- eslint-disable n/prefer-global/process -->
<script setup lang="ts">
import {
  useHotelStore,
  useLabelsStore,
  useUserStore,
} from 'pinia-store'

// ** components
import dayjs from 'dayjs'
import { stripHtml } from '@general-function'
import Litepicker from '@ui/base-components/litepicker'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { useI18n } from '#imports'

const props = defineProps({
  classes: {
    type: String,
    default: '',
  },
  alwaysShow: {
    type: Boolean,
    default: false,
  },
  classesButtonFlight: {
    type: String,
    default: 'border-y-2 border-solid bg-white p-3 uppercase !border-[var(--color-primary)] !text-center !text-[var(--color-primary)]',
  },
})
// ** pinia
const useLabels = useLabelsStore()
const useHotel = useHotelStore()

// ** vars
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = !props.alwaysShow ? breakpoints.smallerOrEqual('sm') : true
const { t } = useI18n()
const now = dayjs()
const start = ref(now.format('DD MMM YYYY'))
const end = ref(now.add(1, 'days').format('DD MMM YYYY'))

const labels = computed(() => useLabels.getLabels)
const profile = computed(() => useHotel.getProfile)
const isBookFlightSet = computed(() => useHotel.getProfile.hotel_flight_url)
const scrollTop = ref(0)
const isBookingFormOpen = ref<boolean>(false)
const isDatepickerOpen = ref<boolean>(false)
const buttonLabel = ref<string>('Book Now')
const useUser = useUserStore()
const payload = ref(useUser.payload)
const btnLabel = stripHtml(labels.value?.ROOMS_PAGE_BTN_LABEL, false, 'Book Now')
const labelCheckIn = stripHtml(props.labels?.HOME_LABEL_CHECK_IN, false, 'Check in / Check out')

function bookingNowCta(event) {
  try {
    event.preventDefault()
    if (process?.client && profile.value?.hotel_simplebooking && payload.value.dates) {
      const bookUrl = new URL(profile.value.hotel_simplebooking)
      const populateAdults = Array(payload.value.adults).fill('A').join(',') // fill 'A', came from simpleBooking
      const populateChilds = payload.value.childs > 0 ? `,${Array(payload.value.childs).fill('2').join(',')}` : '' // default age of child set to 2 age.
      const splitDate = payload.value.dates.split(' - ')
      start.value = dayjs(splitDate[0].toString()).format('YYYY-MM-DD')
      end.value = dayjs(splitDate[1].toString()).format('YYYY-MM-DD')
      bookUrl.searchParams.set('in', start.value)
      bookUrl.searchParams.set('out', end.value)
      bookUrl.searchParams.set('guests', `${encodeURI(populateAdults)}${encodeURI(populateChilds)}`)
      // need to add again after query hash '#q' from simpleBooking
      window.open(`${bookUrl}#q&guests=${encodeURI(populateAdults)}${encodeURI(populateChilds)}&in=${start.value}&out=${end.value}`, '_blank')
    }
  }
  catch (error) {}
}
function showBookingButton() {
  if (process.client && window && document) {
    window.addEventListener('scroll', () => {
      scrollTop.value = window.pageYOffset || window.scrollY
      if (scrollTop.value >= 800) {
        // is already catch the bottom of document?
        if ((window.innerHeight + Math.round(scrollTop.value)) <= (document.body.scrollHeight - 2)) {
          document.getElementsByClassName('fixed-bottom-booking-button')[0].classList.add('animate')
        }
        else {
          // you're at the bottom of the page, remove booking bottom
          isBookingFormOpen.value = false
          isDatepickerOpen.value = false
          document.getElementsByClassName('fixed-bottom-booking-button')[0].classList.remove('animate')
        }
      }
      else {
        isBookingFormOpen.value = false
        isDatepickerOpen.value = false
        document.getElementsByClassName('fixed-bottom-booking-button')[0].classList.remove('animate')
      }
    })
  }
}

function openDatepicker() {
  isDatepickerOpen.value = true
  buttonLabel.value = 'Cancel'
}

function fixedButtonAction() {
  switch ((buttonLabel.value).toLocaleLowerCase()) {
    case 'close':
      isBookingFormOpen.value = false
      buttonLabel.value = 'Book Now'
      break
    case 'cancel':
      isDatepickerOpen.value = false
      buttonLabel.value = 'Close'
      break
    case 'book now':
      isBookingFormOpen.value = true
      buttonLabel.value = 'Close'
      break
    default:
      buttonLabel.value = 'Book Now'
      break
  }
}

// ** watch
watch(isBookingFormOpen, (currentValue) => {
  if (!currentValue) {
    buttonLabel.value = 'Book now'
  }
  else {
    isDatepickerOpen.value = false
    buttonLabel.value = 'Close'
  }
}, { deep: true, immediate: true })

onBeforeMount(() => {
  if (!payload.value.dates || payload.value.dates.length < 3)
    payload.value.dates = `${start.value} - ${end.value}`
})

watch(() => useUser.payload, (newValue) => {
  if (newValue && JSON.stringify(newValue) !== JSON.stringify(payload.value.dates))
    payload.value = useUser.payload
})

watch(() => payload.value.dates, (newValue) => {
  if (newValue)
    isDatepickerOpen.value = false
})

watch(() => payload.value, (newValue) => {
  if (newValue && JSON.stringify(newValue) !== JSON.stringify(useUser.payload))
    useUser.setPayload(payload.value)
})

watch(isDatepickerOpen, (currentValue) => {
  if (!currentValue && isBookingFormOpen.value)
    buttonLabel.value = 'Close'
}, { deep: true, immediate: true })

// ** onMounted
onMounted(() => {
  showBookingButton()
})
</script>

<template>
  <div
    :class="{
      'animate': isBookingFormOpen,
      'datepicker-open': isDatepickerOpen,
      'flex md:hidden': !isMobile,
    }"
    class="fixed-bottom-booking-form font-secondary lg:hidden xl:hidden"
  >
    <div class="fixed-bottom-booking-inner relative">
      <div class="g-0 fixed-bottom-booking-form_bottom">
        <div class="form-group" :class="{ 'form-group-datepicker': isDatepickerOpen }">
          <ClientOnly
            fallback-tag="span"
            fallback="litepicker"
          >
            <Litepicker
              id="fixed-dates"
              v-model="payload.dates"
              :options="{
                autoApply: true,
                allowRepick: true,
                dropdowns: {
                  minYear: (new Date()).getFullYear(),
                  maxYear: (new Date()).getFullYear() + 2,
                  months: true,
                  years: true,
                },
                startDate: new Date(),
                minDate: new Date(),
                format: 'DD MMM YYYY',
                numberOfColumns: 2,
                numberOfMonths: 2,
                singleMode: false,
                tooltipText: {
                  one: 'night',
                  other: 'nights',
                },
                tooltipNumber: (totalDays) => {
                  return totalDays - 1;
                },
                inlineMode: true,
              }"
              class="form-control mb-0 shadow-none"
              name="dates"
              :placeholder="labelCheckIn"
              :readonly="true"
              @focusin="openDatepicker()"
            />
            <ICalendar class="right-[1.25em] fill-secondary"/>
          </ClientOnly>
        </div>
        <div v-show="!isDatepickerOpen" class="mt-2 sm:grid sm:grid-cols-12 sm:gap-4">
          <div class="sm:col-start-1 sm:col-end-7">
            <div class="qty-buttons">
              <label>Adult(s)</label>
              <button type="button" class="btn btn-qty-plus absolute right-10 px-3" @click="payload.adults++">
                <IPlus class="fill-secondary p-0" size="16"/>
              </button>
              <input id="fixed-adults" v-model="payload.adults" type="text" name="adults" class="form-control qty mb-0" autocomplete="off" :readonly="true">
              <button type="button" class="btn btn-qty-minus absolute right-0 top-0 px-3" @click="payload.adults <= 1 ? 1 : payload.adults--">
                <IMinus class="fill-secondary p-0" size="16"/>
              </button>
            </div>
          </div>
          <div class="sm:col-start-7 sm:col-end-14">
            <div class="qty-buttons">
              <label>Kid(s)</label>
              <button type="button" class="btn btn-qty-plus absolute right-10 px-3" @click="payload.childs++">
                <IPlus class="fill-secondary p-0" size="16"/>
              </button>
              <input id="fixed-childs" v-model="payload.childs" type="text" name="childs" class="qty form-control mb-0" autocomplete="off" :readonly="true">
              <button type="button" class="btn btn-qty-minus absolute right-0 top-0 px-3" @click="payload.childs !== 0 ? payload.childs-- : 0">
                <IMinus class="fill-secondary p-0" size="16"/>
              </button>
            </div>
          </div>
          <div class="mt-4 w-full sm:col-start-1 sm:col-end-14">
            <input
              type="submit"
              class="btn_search uppercase"
              :value="btnLabel"
              :title="btnLabel"
              @click="(event: MouseEvent) => bookingNowCta(event)"
            >
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    :class="{
      'md:!hidden': !isMobile,
      [props.classes]: true,
    }"
    class="font-secondary fixed-bottom-booking-button lg:!hidden xl:!hidden"
  >
    <NuxtLink
      v-if="isBookFlightSet"
      :class="`${isBookFlightSet ? 'w-1/2' : ''} ${classesButtonFlight}`"
      :to="profile.hotel_flight_url"
      target="_blank"
      rel="noreferrer noopener"
    >
      {{ t('book_flight') }}
    </NuxtLink>
    <button
      :title="buttonLabel"
      class="p-3 uppercase"
      :class="{
        'w-1/2': isBookFlightSet,
        'w-full': !isBookFlightSet,
      }"
      @click="fixedButtonAction()"
      v-html="buttonLabel"
    >
    </button>
  </div>
</template>

<style lang="scss">
@import './button-litepicker/component.scss';
</style>
